import React from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { Card } from "../components/ui/card";
import {
  PieChart, Pie, Cell, Tooltip, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  LineChart, Line,
} from "recharts";
import { Users, UserCheck, Building2, TrendingUp, TrendingDown, ArrowUpRight, FileText, LogIn, Star } from "lucide-react";
import { toast } from "sonner";
import Avatar from "../components/Avatar";
import DeferredMount from "../components/DeferredMount";
import { useAuth } from "../contexts/AuthContext";

const PALETTE = ["#C97B63", "#7A9B76", "#D4A04C", "#6B8CAE", "#B85042", "#E8D5C4", "#C5D4C0"];

export default function Dashboard() {
  const navigate = useNavigate();
  const { loginAsPersona } = useAuth();
  const { data, isLoading } = useQuery({
    queryKey: ["dashboard"],
    queryFn: async () => (await api.get("/stats/dashboard")).data.data,
    refetchInterval: 15000,
  });

  // Build per-row contextual menu shown on avatar click. Personas get extra
  // demo-only quick actions ("Détails persona" + "Connexion en tant que").
  const buildActions = (u) => {
    const acts = [
      {
        label: "Voir la fiche RH",
        icon: <FileText size={13} />,
        testKey: "fiche-rh",
        onClick: () => navigate(`/users?focus=${u.id}`),
      },
    ];
    if (u.persona) {
      acts.push({
        label: "Détails persona",
        icon: <Star size={13} />,
        testKey: "persona-details",
        onClick: () => navigate("/personas"),
      });
      acts.push({
        label: "Connexion en tant que",
        icon: <LogIn size={13} />,
        testKey: "login-as",
        onClick: async () => {
          try {
            const ok = await loginAsPersona(u.persona, { remember: false });
            if (ok) {
              toast.success(`Connecté en tant que ${u.display_name}`);
              navigate("/");
            } else {
              toast.error("Échec de la connexion en tant que persona");
            }
          } catch (e) {
            toast.error(`Erreur : ${e.message || "inconnue"}`);
          }
        },
      });
    }
    return acts;
  };

  if (isLoading || !data) {
    return <div className="p-10 text-warm-dark/60">Chargement…</div>;
  }

  const k = data.kpis;
  const monthlyDelta = data.monthly_delta || 0;
  const arrivees = data.arrivees_mois || 0;
  const departs = data.departs_mois || 0;

  return (
    <div className="p-8 max-w-[1600px] mx-auto" data-testid="dashboard-page">
      <div className="flex items-end justify-between mb-7">
        <div>
          <h1 className="font-heading text-[48px] font-semibold text-warm-dark tracking-tight leading-tight">Tableau de bord</h1>
          <p className="text-[15px] text-warm-dark/70 mt-1">Population Maisonéa en temps réel · {new Date().toLocaleDateString("fr-FR", { dateStyle: "full" })}</p>
        </div>
      </div>

      {/* KPIs top row */}
      <div className="grid grid-cols-4 gap-4 mb-5">
        <Kpi icon={Users} label="Total collaborateurs" value={k.total_users} delta={monthlyDelta} />
        <Kpi icon={UserCheck} label="Effectif actif" value={k.active_users} />
        <Kpi
          icon={TrendingUp}
          label="Mouvements du mois"
          value={`${arrivees} / ${departs}`}
          hint={`${arrivees} arrivée(s) · ${departs} départ(s)`}
        />
        <Kpi icon={Building2} label="Sites actifs" value={k.total_sites} />
      </div>

      <div className="grid grid-cols-2 gap-4 mb-5">
        <Card className="p-6 border-sand shadow-soft rounded-xl bg-off-white min-w-0">
          <ChartHeader title="Effectif par région" subtitle="Top régions" />
          <div className="h-64">
            {data.region_chart?.length ? (
              <DeferredMount>
                {(w, h) => (
                  <BarChart width={w} height={h} data={data.region_chart} layout="vertical" margin={{ left: 20 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#EBDDC8" horizontal={false} />
                    <XAxis type="number" tick={{ fontSize: 11, fill: "#2C2420", opacity: 0.6 }} />
                    <YAxis dataKey="label" type="category" width={100} tick={{ fontSize: 11, fill: "#2C2420" }} />
                    <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8, border: "1px solid #EBDDC8", background: "#FBF8F3" }} />
                    <Bar dataKey="count" fill="#C97B63" radius={[0, 4, 4, 0]} />
                  </BarChart>
                )}
              </DeferredMount>
            ) : <EmptyChart />}
          </div>
        </Card>

        <Card className="p-6 border-sand shadow-soft rounded-xl bg-off-white min-w-0">
          <ChartHeader title="Répartition par type de contrat" />
          <div className="h-64">
            {data.contract_chart?.length ? (
              <DeferredMount>
                {(w, h) => (
                  <PieChart width={w} height={h}>
                    <Pie data={data.contract_chart} dataKey="count" nameKey="label" innerRadius={60} outerRadius={95} paddingAngle={3}>
                      {data.contract_chart.map((entry, i) => <Cell key={entry.label} fill={PALETTE[i % PALETTE.length]} />)}
                    </Pie>
                    <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8, border: "1px solid #EBDDC8", background: "#FBF8F3" }} />
                  </PieChart>
                )}
              </DeferredMount>
            ) : <EmptyChart />}
          </div>
          <div className="flex flex-wrap gap-3 mt-3">
            {(data.contract_chart || []).map((c, i) => (
              <div key={c.label} className="flex items-center gap-1.5 text-[12px]">
                <span className="w-2.5 h-2.5 rounded-sm" style={{ background: PALETTE[i % PALETTE.length] }} />
                <span className="text-warm-dark">{c.label}</span>
                <span className="text-warm-dark/50">· {c.count}</span>
              </div>
            ))}
          </div>
        </Card>
      </div>

      <div className="grid grid-cols-2 gap-4 mb-5">
        <Card className="p-6 border-sand shadow-soft rounded-xl bg-off-white min-w-0">
          <ChartHeader title="Top 5 magasins par effectif" />
          <div className="h-56">
            {data.site_chart?.length ? (
              <DeferredMount>
                {(w, h) => (
                  <BarChart width={w} height={h} data={(data.site_chart || []).slice(0, 5)}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#EBDDC8" />
                    <XAxis dataKey="label" tick={{ fontSize: 10, fill: "#2C2420" }} angle={-15} textAnchor="end" height={60} />
                    <YAxis tick={{ fontSize: 11, fill: "#2C2420", opacity: 0.6 }} />
                    <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8, border: "1px solid #EBDDC8", background: "#FBF8F3" }} />
                    <Bar dataKey="count" fill="#7A9B76" radius={[4, 4, 0, 0]} />
                  </BarChart>
                )}
              </DeferredMount>
            ) : <EmptyChart />}
          </div>
        </Card>
        <Card className="p-6 border-sand shadow-soft rounded-xl bg-off-white min-w-0">
          <ChartHeader title="Activité API — dernière heure" subtitle="Appels / minute" />
          <div className="h-56">
            {data.api_activity?.length ? (
              <DeferredMount>
                {(w, h) => (
                  <LineChart width={w} height={h} data={data.api_activity}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#EBDDC8" />
                    <XAxis dataKey="minute" tick={{ fontSize: 10, fill: "#2C2420", opacity: 0.6 }} />
                    <YAxis tick={{ fontSize: 11, fill: "#2C2420", opacity: 0.6 }} />
                    <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8, border: "1px solid #EBDDC8", background: "#FBF8F3" }} />
                    <Line type="monotone" dataKey="count" stroke="#C97B63" strokeWidth={2} dot={false} />
                  </LineChart>
                )}
              </DeferredMount>
            ) : <EmptyChart label="Démarrez du trafic API pour voir apparaître la courbe" />}
          </div>
        </Card>
      </div>

      <Card className="p-6 border-sand shadow-soft rounded-xl bg-off-white">
        <ChartHeader title="Activité récente" subtitle="Derniers événements RH" />
        <div className="divide-y divide-sand">
          {(data.recent_users || []).length === 0 ? (
            <div className="py-8 text-center text-warm-dark/50 text-[13px]">Aucun événement</div>
          ) : (data.recent_users || []).slice(0, 15).map((u) => (
            <div key={u.id} className="flex items-center justify-between py-3" data-testid={`activity-${u.id}`}>
              <div className="flex items-center gap-3">
                <Avatar
                  name={u.display_name}
                  email={u.email}
                  seed={u.persona || u.email}
                  size="sm"
                  actions={buildActions(u)}
                  menuLabel={u.display_name}
                />
                <div>
                  <div className="text-[14px] font-medium text-warm-dark flex items-center gap-1.5">
                    {u.display_name}
                    {u.persona && <span className="px-1.5 py-0.5 bg-terracotta-light text-terracotta text-[10px] rounded font-medium">Persona</span>}
                  </div>
                  <div className="text-[12px] text-warm-dark/60 font-mono">{u.login} · {u.mail}</div>
                </div>
              </div>
              <div className="text-[11px] text-warm-dark/50 font-mono">{u.updated_at ? new Date(u.updated_at).toLocaleString("fr-FR") : ""}</div>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}

function Kpi({ icon: Icon, label, value, delta, hint }) {
  const deltaPositive = typeof delta === "number" && delta >= 0;
  return (
    <Card className="p-5 border-sand shadow-soft rounded-xl bg-off-white" data-testid={`kpi-${label}`}>
      <div className="flex items-start justify-between">
        <div>
          <div className="text-[11px] font-semibold uppercase tracking-wider text-warm-dark/60">{label}</div>
          <div className="font-heading text-[36px] font-semibold text-warm-dark mt-2 leading-none">{typeof value === "number" ? value.toLocaleString("fr-FR") : value}</div>
          {typeof delta === "number" && (
            <div className={`flex items-center gap-1 mt-2 text-[12px] font-medium ${deltaPositive ? "text-sage" : "text-error"}`}>
              {deltaPositive ? <TrendingUp size={12} strokeWidth={1.5} /> : <TrendingDown size={12} strokeWidth={1.5} />}
              {deltaPositive ? "+" : ""}{delta} vs mois précédent
            </div>
          )}
          {hint && <div className="text-[11px] text-warm-dark/60 mt-1.5">{hint}</div>}
        </div>
        <div className="w-9 h-9 rounded-lg bg-terracotta-light flex items-center justify-center">
          <Icon size={16} strokeWidth={1.5} className="text-terracotta" />
        </div>
      </div>
    </Card>
  );
}

function ChartHeader({ title, subtitle }) {
  return (
    <div className="flex items-center justify-between mb-4">
      <div>
        <div className="text-[11px] font-semibold uppercase tracking-wider text-warm-dark/60">{title}</div>
        {subtitle && <div className="font-heading text-[18px] font-semibold text-warm-dark mt-0.5">{subtitle}</div>}
      </div>
      <ArrowUpRight size={14} strokeWidth={1.5} className="text-warm-dark/30" />
    </div>
  );
}

function EmptyChart({ label = "Aucune donnée" }) {
  return <div className="h-full flex items-center justify-center text-warm-dark/40 text-[13px]">{label}</div>;
}
